import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { Dialog, DialogContent } from './ui/dialog'
import { Button } from './ui/button'
import { Input } from './ui/input'

/**
 * SearchOverlay is full-text search over every message this user has ever
 * sent or received — internal/repo.Search, FTS5-backed.
 *
 * Search-as-you-type, debounced: a query fires 300ms after the last
 * keystroke rather than on every one, which is enough that typing "deploy"
 * costs one request instead of six.
 *
 * Built on Dialog rather than shadcn's Command: Command owns its own
 * client-side filtering state, which doesn't fit an async, server-debounced
 * search with a custom full-screen mobile layout — a plain Dialog with the
 * existing custom result list ports more faithfully.
 */
export function SearchOverlay({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [text, setText] = useState('')
  const [debounced, setDebounced] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    if (!open) {
      setText('')
      setDebounced('')
    }
  }, [open])

  useEffect(() => {
    const id = setTimeout(() => setDebounced(text.trim()), 300)
    return () => clearTimeout(id)
  }, [text])

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => api.search(debounced),
    enabled: debounced.length > 0,
  })

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        showCloseButton={false}
        className="flex h-full max-h-full w-full max-w-full flex-col gap-0 rounded-none p-0 sm:top-[10%] sm:h-auto sm:max-h-[70vh] sm:w-[32rem] sm:max-w-[32rem] sm:translate-y-0 sm:rounded-lg"
        data-testid="search-overlay"
      >
        <div className="flex items-center gap-2 border-b p-3">
          {/* On a phone this panel fills the screen, so the backdrop click
              that closes it on desktop is unreachable — there is nothing
              behind the panel to click. This button is the only way back. */}
          <Button
            className="inline-flex sm:hidden"
            variant="ghost"
            size="icon-sm"
            aria-label="Close search"
            onClick={onClose}
            data-testid="search-close"
          >
            ←
          </Button>
          <Input
            autoFocus
            placeholder="Search your messages…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="search-input"
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          {isFetching ? (
            <div className="flex justify-center py-6">
              <div className="size-4 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
            </div>
          ) : debounced.length === 0 ? (
            <p className="p-4 text-center text-sm text-muted-foreground">
              Type to search across every conversation.
            </p>
          ) : results.length === 0 ? (
            <p className="p-4 text-center text-sm text-muted-foreground">No messages match “{debounced}”.</p>
          ) : (
            results.map((r) => (
              <button
                key={r.message_id}
                type="button"
                className="block w-full px-4 py-3 text-left hover:bg-muted"
                onClick={() => {
                  onClose()
                  navigate(`/c/${r.conversation_id}`)
                }}
                data-testid="search-result"
              >
                <p className="mb-1 text-xs text-muted-foreground">
                  {r.conversation_title || 'Untitled'} · {r.role}
                </p>
                <p className="text-sm">{r.snippet}</p>
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
